<script setup>
import { computed } from 'vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisToggle from 'dashboard/components-next/LaisToggle.vue';
import { FIELD_INPUT_CLASS, FIELD_TEXTAREA_CLASS } from '../constants';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * The screen the customer sees right after submitting. Authoring it here (and
 * not in an ERB template) is what makes the public page fully tenant-owned.
 */
const props = defineProps({
  successScreen: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);

const { t, validationMessage } = useSurveyLabels();

const title = computed({
  get: () => props.successScreen.title,
  set: value => emit('update', { title: value }),
});

const message = computed({
  get: () => props.successScreen.message,
  set: value => emit('update', { message: value }),
});

const ctaLabel = computed({
  get: () => props.successScreen.cta_label,
  set: value => emit('update', { cta_label: value }),
});

const ctaUrl = computed({
  get: () => props.successScreen.cta_url,
  set: value => emit('update', { cta_url: value }),
});

const showScore = computed({
  get: () => props.successScreen.show_score,
  set: value => emit('update', { show_score: value }),
});
</script>

<template>
  <section class="grid gap-4">
    <header class="min-w-0">
      <h2
        class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        {{ t('SURVEYS.EDITOR.SUCCESS.TITLE') }}
      </h2>
      <p class="mt-1 text-lais-body-sm text-lais-text-body">
        {{ t('SURVEYS.EDITOR.SUCCESS.SUBTITLE') }}
      </p>
    </header>

    <LaisField
      :label="t('SURVEYS.EDITOR.SUCCESS.HEADLINE')"
      :error="validationMessage(errors.title)"
      required
    >
      <input
        v-model="title"
        type="text"
        :class="FIELD_INPUT_CLASS"
        :placeholder="t('SURVEYS.EDITOR.SUCCESS.HEADLINE_PLACEHOLDER')"
      />
    </LaisField>

    <LaisField
      :label="t('SURVEYS.EDITOR.SUCCESS.MESSAGE')"
      :helper="t('SURVEYS.EDITOR.SUCCESS.MESSAGE_HELPER')"
    >
      <textarea
        v-model="message"
        rows="3"
        :class="FIELD_TEXTAREA_CLASS"
        :placeholder="t('SURVEYS.EDITOR.SUCCESS.MESSAGE_PLACEHOLDER')"
      />
    </LaisField>

    <div class="grid gap-4 md:grid-cols-2">
      <LaisField
        :label="t('SURVEYS.EDITOR.SUCCESS.CTA_LABEL')"
        :helper="t('SURVEYS.EDITOR.SUCCESS.CTA_HELPER')"
        :error="validationMessage(errors.cta_label)"
      >
        <input
          v-model="ctaLabel"
          type="text"
          :class="FIELD_INPUT_CLASS"
          :placeholder="t('SURVEYS.EDITOR.SUCCESS.CTA_LABEL_PLACEHOLDER')"
        />
      </LaisField>

      <LaisField
        :label="t('SURVEYS.EDITOR.SUCCESS.CTA_URL')"
        :error="validationMessage(errors.cta_url)"
      >
        <input
          v-model="ctaUrl"
          type="url"
          inputmode="url"
          :class="FIELD_INPUT_CLASS"
          :placeholder="t('SURVEYS.EDITOR.SUCCESS.CTA_URL_PLACEHOLDER')"
        />
      </LaisField>
    </div>

    <LaisToggle
      v-model="showScore"
      :label="t('SURVEYS.EDITOR.SUCCESS.SHOW_SCORE')"
      :helper="t('SURVEYS.EDITOR.SUCCESS.SHOW_SCORE_HELPER')"
    />
  </section>
</template>
